import axios, { AxiosRequestConfig } from 'axios'
import { useAuthStore } from '@/stores/auth'
import { useLoadingStore } from '@/stores/loading'
import { useCacheStore } from '@/stores/cache'
import { csrfManager } from '../csrfManager'
import { networkService } from '../../network'
import { API_CONFIG } from '../config'
import { isNativeApp } from '../../device'
import { trackCacheHit } from '../metrics'

/**
 * Verifica se a requisição é para uma rota web (Sanctum) vs API
 */
function isWebRoute(url?: string, baseURL?: string): boolean {
  if (!url) return false
  if (baseURL && !baseURL.includes('/api')) {
    return true
  }
  const fullUrl = baseURL ? `${baseURL}${url}` : url
  return !fullUrl.includes('/api/')
}

/**
 * Verifica se o método HTTP requer CSRF token
 */
function requiresCsrfToken(method?: string): boolean {
  if (!method) return false
  const upperMethod = method.toUpperCase()
  return ['POST', 'PUT', 'PATCH', 'DELETE'].includes(upperMethod)
}

/**
 * Gera uma chave de cache para a requisição
 */
function generateCacheKey(config: AxiosRequestConfig): string {
  const url = config.url || ''
  const params = config.params ? JSON.stringify(config.params) : ''
  return `${config.method}:${url}:${params}`
}

/**
 * Verifica se deve usar cache para esta requisição
 */
function shouldUseCache(config: AxiosRequestConfig): boolean {
  // Apenas GET pode ter cache
  if (config.method !== 'get') return false

  const url = config.url || ''

  // Verificar explicitamente desabilitado
  if (config.metadata?.skipCache) return false

  // Verificar se endpoint não deve ter cache
  if (!API_CONFIG.shouldCache(url)) return false

  // Verificar metadata da requisição
  if (config.metadata?.cache === false) return false

  return true
}

export const setupRequestInterceptor = (axiosInstance: any) => {
  axiosInstance.interceptors.request.use(
    async (config: AxiosRequestConfig) => {
      const authStore = useAuthStore()
      const loadingStore = useLoadingStore()
      const cacheStore = useCacheStore()
      const isNative = isNativeApp()

      // === DETECÇÃO DE REDE OFFLINE ===
      if (networkService.isOffline()) {
        // Se offline, tentar usar cache
        if (config.method === 'get') {
          const cacheKey = config.metadata?.cacheKey || generateCacheKey(config)
          const cached = cacheStore.get(cacheKey)

          if (cached) {
            if (import.meta.env.DEV) {
              console.log('[Cache] Usando dados offline do cache:', cacheKey)
            }

            trackCacheHit(true)

            // Retornar como se fosse uma resposta bem-sucedida
            return Promise.reject({
              ...new Error('Offline - using cache'),
              config,
              response: {
                data: cached,
                status: 200,
                statusText: 'OK (offline)',
                headers: {},
                config,
                _fromCache: true,
                _offline: true,
              },
              _isOfflineCache: true,
            })
          }
        }

        // Se não tem cache e está offline, rejeitar com erro específico
        return Promise.reject({
          ...new Error('Sem conexão com a internet'),
          config,
          _isOffline: true,
          code: 'NETWORK_OFFLINE',
        })
      }

      // === AUTH TOKEN ===
      const token = authStore.token || (typeof localStorage !== 'undefined' ? localStorage.getItem('auth_token') : null)
      if (token) {
        config.headers = config.headers || {}
        config.headers.Authorization = `Bearer ${token}`
      }

      // === CSRF TOKEN ===
      if (!isNative && requiresCsrfToken(config.method)) {
        // Usar csrfManager - aguardar token se necessário para evitar 419
        let csrfToken = csrfManager.getTokenSync()
        if (!csrfToken) {
          csrfToken = await csrfManager.getToken()
        }
        if (csrfToken) {
          config.headers = config.headers || {}
          config.headers['X-XSRF-TOKEN'] = csrfToken
        } else if (import.meta.env.DEV) {
          console.warn('[CSRF] Token não encontrado. Cookies disponíveis:', typeof document !== 'undefined' ? document.cookie : 'N/A')
        }
      }

      // === CACHE-FIRST PARA ENDPOINTS CONFIGURADOS ===
      if (shouldUseCache(config)) {
        const cacheKey = config.metadata?.cacheKey || generateCacheKey(config)

        // Se é cache-first e tem dados válidos, retornar do cache
        if (config.metadata?.cacheFirst !== false && API_CONFIG.shouldUseCacheFirst(config.url || '')) {
          const cached = cacheStore.get(cacheKey)
          if (cached) {
            if (import.meta.env.DEV) {
              console.log('[Cache] Cache-first hit:', cacheKey)
            }

            trackCacheHit(true)

            return Promise.reject({
              ...new Error('Cache hit'),
              config,
              response: {
                data: cached,
                status: 200,
                statusText: 'OK (cached)',
                headers: {},
                config,
                _fromCache: true,
              },
              _isCacheHit: true,
            })
          }
        }

        // Marcar para salvar no cache se for bem-sucedido
        config.metadata = config.metadata || {}
        config.metadata.cache = true
        config.metadata.cacheKey = cacheKey
        config.metadata.cacheTTL = config.metadata.cacheTTL || API_CONFIG.getCacheTTL(config.url || '')
      }

      // === TIMEOUT DINÂMICO ===
      config.timeout = config.metadata?.timeout || API_CONFIG.getTimeout(config.url, isNative)

      // === HEADERS PADRÃO ===
      const existingContentType = config.headers?.['Content-Type']
      config.headers = {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        ...(isNative ? {} : { 'X-Requested-With': 'XMLHttpRequest' }),
        ...config.headers,
      }

      // Preservar multipart/form-data
      if (existingContentType === 'multipart/form-data') {
        delete config.headers['Content-Type']
      }

      // === CREDENTIALS ===
      config.withCredentials = !isNative

      // === METADATA ===
      config.metadata = {
        ...config.metadata,
        startTime: Date.now(),
        isNative,
        connectionType: networkService.getConnectionType(),
      }

      // === LOADING ===
      if (!config.metadata?.background) {
        loadingStore.startLoading(config.metadata?.key || 'global')
      }

      return config
    },
    (error) => {
      const loadingStore = useLoadingStore()
      loadingStore.stopLoading('global')

      console.error('[Request Interceptor Error]', {
        message: error.message,
        config: {
          url: error.config?.url,
          baseURL: error.config?.baseURL,
          method: error.config?.method,
        },
      })

      return Promise.reject(error)
    }
  )
}

/**
 * Exporta função para obter token CSRF manualmente se necessário
 */
export async function ensureCsrfToken(): Promise<string | null> {
  return csrfManager.getToken()
}

/**
 * Invalida o cache de CSRF
 */
export function invalidateCsrfToken(): void {
  csrfManager.invalidate()
}
